/**
 * Tracked setters for scale drop form (mark user edits).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, type MutableRefObject } from 'react';

export interface ScaleDropFormSetters {
  setAmount: (v: number | '' | ((p: number | '') => number | '')) => void;
  setRouteId: (v: number | ((p: number) => number)) => void;
  setTldValue: (v: string | ((p: string) => string)) => void;
  setLeadsListWiserId: (v: number | ((p: number) => number)) => void;
  setSelectedSenderNames: (v: string[] | ((p: string[]) => string[])) => void;
  setSelectedSmsTexts: (v: string[] | ((p: string[]) => string[])) => void;
  setSelectedStaticTagId: (v: string | null | 'unchanged') => void;
  setLocalSenders: (v: string[] | ((p: string[]) => string[])) => void;
  setLocalTexts: (v: string[] | ((p: string[]) => string[])) => void;
}

export interface ScaleDropFormTrackedSetters {
  setAmountTracked: ScaleDropFormSetters['setAmount'];
  setRouteIdTracked: ScaleDropFormSetters['setRouteId'];
  setTldValueTracked: ScaleDropFormSetters['setTldValue'];
  setLeadsListWiserIdTracked: ScaleDropFormSetters['setLeadsListWiserId'];
  setSelectedSenderNamesTracked: ScaleDropFormSetters['setSelectedSenderNames'];
  setSelectedSmsTextsTracked: ScaleDropFormSetters['setSelectedSmsTexts'];
  setSelectedStaticTagIdTracked: ScaleDropFormSetters['setSelectedStaticTagId'];
  setLocalSendersTracked: ScaleDropFormSetters['setLocalSenders'];
  setLocalTextsTracked: ScaleDropFormSetters['setLocalTexts'];
}

export function useScaleDropFormTrackedSetters(
  hasUserEditedRef: MutableRefObject<boolean>,
  setters: ScaleDropFormSetters,
): ScaleDropFormTrackedSetters {
  const setAmountTracked = useCallback(
    (v: number | '' | ((p: number | '') => number | '')) => {
      hasUserEditedRef.current = true;
      setters.setAmount(v);
    },
    [setters.setAmount],
  );
  const setRouteIdTracked = useCallback(
    (v: number | ((p: number) => number)) => {
      hasUserEditedRef.current = true;
      setters.setRouteId(v);
    },
    [setters.setRouteId],
  );
  const setTldValueTracked = useCallback(
    (v: string | ((p: string) => string)) => {
      hasUserEditedRef.current = true;
      setters.setTldValue(v);
    },
    [setters.setTldValue],
  );
  const setLeadsListWiserIdTracked = useCallback(
    (v: number | ((p: number) => number)) => {
      hasUserEditedRef.current = true;
      setters.setLeadsListWiserId(v);
    },
    [setters.setLeadsListWiserId],
  );
  const setSelectedSenderNamesTracked = useCallback(
    (v: string[] | ((p: string[]) => string[])) => {
      hasUserEditedRef.current = true;
      setters.setSelectedSenderNames(v);
    },
    [setters.setSelectedSenderNames],
  );
  const setSelectedSmsTextsTracked = useCallback(
    (v: string[] | ((p: string[]) => string[])) => {
      hasUserEditedRef.current = true;
      setters.setSelectedSmsTexts(v);
    },
    [setters.setSelectedSmsTexts],
  );
  const setSelectedStaticTagIdTracked = useCallback(
    (v: string | null | 'unchanged') => {
      hasUserEditedRef.current = true;
      setters.setSelectedStaticTagId(v);
    },
    [setters.setSelectedStaticTagId],
  );
  const setLocalSendersTracked = useCallback(
    (v: string[] | ((p: string[]) => string[])) => {
      hasUserEditedRef.current = true;
      setters.setLocalSenders(v);
    },
    [setters.setLocalSenders],
  );
  const setLocalTextsTracked = useCallback(
    (v: string[] | ((p: string[]) => string[])) => {
      hasUserEditedRef.current = true;
      setters.setLocalTexts(v);
    },
    [setters.setLocalTexts],
  );

  return {
    setAmountTracked,
    setRouteIdTracked,
    setTldValueTracked,
    setLeadsListWiserIdTracked,
    setSelectedSenderNamesTracked,
    setSelectedSmsTextsTracked,
    setSelectedStaticTagIdTracked,
    setLocalSendersTracked,
    setLocalTextsTracked,
  };
}
